import React from 'react';
import PrimaryButton from 'components/buttons/PrimaryButton';
import SecondaryButton from 'components/buttons/SecondaryButton';
import { IntegrationPageActionsProps } from '../IntegrationPage';
import OrderIntegrationButton from '../OrderIntegrationButton';
import DeactivateIntegrationButton from '../DeactivateIntegrationButton';
import { useOrderableModuleFeatures } from '../../billing/functions/useOrderableModuleFeatures';
import { useTranslation } from 'react-i18next';
import { useModal } from 'components/modals/ModalContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';

const EBoekhoudenIntegrationPageActions: React.FC<IntegrationPageActionsProps> = ({ 
    response, 
    form,
    integrationSlug,
    integrationId,
    featureKey,
    handleRefetch
}) => {
    const { t } = useTranslation();
    const { initializeModal } = useModal();
    const { environmentType } = useAuthContext();

    // Get the form component
    const FormComponent = form;

    // Determine whether the paid integration feature is active
    const moduleFeatures = useOrderableModuleFeatures();
    const isFeatureActive = featureKey ? environmentType === 'trial' || !!moduleFeatures?.availableFeatures.includes(featureKey) : true;

    // Open the integration form
    const openIntegrationForm = async (integrationId?: number) => {
        if (!form) return;
        initializeModal(
            React.cloneElement(<FormComponent />), {
                title: t('integrations.general.configure_integration_title'),
                itemId: integrationId,
                modalSize: 'small-medium',
                onSuccess: () => {
                    if (handleRefetch) {
                        handleRefetch();
                    }
                }
            }
        );
    };

    // Wait for the module features before showing the actions
    if (featureKey && environmentType !== 'trial' && !moduleFeatures) {
        return null;
    }

    return (
        <div className="integration-actions">
            {featureKey && !isFeatureActive ? (
                <OrderIntegrationButton
                    featureKey={featureKey}
                    onOrdered={handleRefetch}
                />
            ) : !response ? (
                <PrimaryButton
                    label="integrations.general.activate_integration_label"
                    onClick={() => openIntegrationForm()}
                />
            ) : (
                <>
                    <SecondaryButton
                        label="integrations.general.edit_integration_label"
                        onClick={() => openIntegrationForm(integrationId)}
                    />
                    {response?.should_reconnect && (
                        <PrimaryButton
                            label="integrations.general.reconnect_integration_label"
                            onClick={() => openIntegrationForm(integrationId)}
                        />
                    )}
                    {featureKey && environmentType !== 'trial' && (
                        <DeactivateIntegrationButton
                            featureKey={featureKey}
                            onDeactivated={handleRefetch}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default EBoekhoudenIntegrationPageActions;